import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import { Tooltip } from '@mui/material_modern'

import DataGridWithSearchV2 from 'components/generic/datagrid/DataGridWithSearchV2'
import StyledLink from 'components/generic/typography/StyledLink'

import { dateTimeFormatter } from 'utils/data-manipulation/valueFormatter'

const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
  },
}))

const getColumns = timezone => [
  {
    field: 'externalId',
    headerName: 'Adj #',
    flex: 1,
    sortable: false,
    renderCell: params => (
      <StyledLink
        to={params?.row?.adjustmentLink}
        onClick={event => {
          event.stopPropagation()
        }}
      >
        {params?.row?.externalId}
      </StyledLink>
    ),
  },
  {
    field: 'updatedAt',
    headerName: 'Last Updated',
    flex: 1,
    renderCell: params => {
      const { updatedAt } = params?.row || {}
      if (updatedAt) {
        return dateTimeFormatter(updatedAt, { timeZone: timezone })
      } else {
        return '--'
      }
    },
    sortComparator: (param1, param2) => {
      const timestamp1 = param1 ? param1.getTime() : 0
      const timestamp2 = param2 ? param2.getTime() : 0
      return timestamp1 - timestamp2
    },
  },
  {
    field: 'effectiveDate',
    headerName: 'Effective Date',
    flex: 1,
    renderCell: params => {
      const { effectiveDate } = params?.row || {}
      if (effectiveDate) {
        return dateTimeFormatter(effectiveDate, { timeZone: timezone })
      } else {
        return '--'
      }
    },
    sortComparator: (param1, param2) => {
      const timestamp1 = param1 ? param1.getTime() : 0
      const timestamp2 = param2 ? param2.getTime() : 0
      return timestamp1 - timestamp2
    },
  },
  {
    field: 'material',
    headerName: 'Material',
    flex: 2,
    sortable: false,
    renderCell: params => {
      const { materials, material } = params.row
      if (material !== 'Multiple') return material
      return (
        <Tooltip title={materials}>
          <div>Multiple</div>
        </Tooltip>
      )
    },
  },
  {
    field: 'wipChange',
    headerName: 'WIP Change',
    headerAlign: 'right',
    align: 'right',
    flex: 1,
    sortable: false,
  },
  {
    field: 'avgCostChange',
    headerName: 'Avg. Cost Change',
    headerAlign: 'right',
    align: 'right',
    flex: 1,
    sortable: false,
  },
]

const AdjustmentsListGrid = props => {
  const navigate = useNavigate()
  const { defaultYardTimezone } = useSelector(state => state.auth)
  const timezone = defaultYardTimezone ?? 'UTC'
  const columns = getColumns(timezone)
  const classes = useStyles()

  const handleRowClick = row => {
    navigate(`/app/inventory/adjustments/${row.id}`)
  }

  return (
    <DataGridWithSearchV2
      {...props}
      className={classes.root}
      columns={columns}
      textSearchPlaceholder="Search for adjustments"
      onRowClick={handleRowClick}
      autoPageSize={false}
    />
  )
}

AdjustmentsListGrid.propTypes = DataGridWithSearchV2.propTypes

AdjustmentsListGrid.defaultProps = DataGridWithSearchV2.defaultProps

export default AdjustmentsListGrid
